import type { CommentListOrder, Comment as CommentType } from '@/@types/comment'
import Comment, { type CommentData } from '@/components/Comment/Comment'
import { CommentActionsProvider } from '@/components/Comment/CommentActionsContext'
import type { ReplyToComment } from '@/components/Comment/types'
import { showCommentFormModal } from '@/components/Comment/utils'
import Page from '@/components/Page'
import CommentListSection from '@/components/Post/CommentListSection'
import { checkIsInJikeApp } from '@/utils/env'
import {
	generateNonPrimaryCommentsQueryKey,
	getCommentQueryOptions,
	getNonPrimaryCommentsQueryOptions,
} from '@/utils/fetchers/comment'
import { fetchPost } from '@/utils/fetchers/post'
import useIsRouteCached from '@/utils/hooks/useIsRouteCached'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { prepareToRefetchListQuery } from '@/utils/list'
import queryClient from '@/utils/queryClient'
import { POST_TYPE_TO_ROUTE_MAPPING, ROUTE_TO_POST_TYPE_MAPPING } from '@/utils/route'
import { css } from '@emotion/react'
import { modals } from '@mantine/modals'
import { Button } from '@ruguoapp/jike-fe-components'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { flatMap, map, pick } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { useTitle } from 'react-use'

export const Route = createFileRoute('/u/$username_/$type/$id/$commentId')({
	component: CommentPage,
	beforeLoad: async ({ matches }) => {
		checkIsInJikeApp(matches)
	},
	loaderDeps: ({ search }) => search,
	loader: async (ctx) => {
		const params = ctx.params as {
			id: string
			username: string
			commentId: string
			type: keyof typeof ROUTE_TO_POST_TYPE_MAPPING
		}
		const [post, comment] = await Promise.all([
			fetchPost(params.id, ROUTE_TO_POST_TYPE_MAPPING[params.type]),
			queryClient.ensureQueryData(
				getCommentQueryOptions({
					id: params.commentId,
					targetType: ROUTE_TO_POST_TYPE_MAPPING[params.type],
				})
			),
		])

		return {
			data: comment,
			post,
		}
	},
})

// comment page

function CommentPage() {
	const { commentId, id, username } = Route.useParams()
	const { data, post } = Route.useLoaderData()
	const isPostPageRouteCached = useIsRouteCached('/u/$username/$type/$id')
	const isViewPostButtonShow = !isPostPageRouteCached

	const currentUser = useCurrentUser()

	const isReplyToSelf = data?.user.id === currentUser.id
	const isCommentFormShow = !isReplyToSelf

	const [order, setOrder] = useState<CommentListOrder>('LIKES')

	useTitle(`${data.user.screenName}：${data.content} - 即刻`)
	const {
		data: commentPages,
		fetchNextPage,
		isLoading: isCommentListLoading,
		refetch,
	} = useInfiniteQuery(
		getNonPrimaryCommentsQueryOptions({
			targetType: data.targetType,
			primaryCommentId: data.threadId,
			level: data.level,
			targetId: data.targetId,
			order,
		})
	)

	const [dummyComments, setDummyComments] = useState<CommentType[]>([])
	const comments = useMemo(
		() =>
			map(
				[...dummyComments, ...flatMap(commentPages?.pages, (page) => page.data)],
				(item) => pick(item, ['id', 'targetType']) as CommentData
			),
		[commentPages?.pages, dummyComments]
	)

	const refresh = useCallback(() => {
		prepareToRefetchListQuery(
			generateNonPrimaryCommentsQueryKey({
				targetType: data.targetType,
				primaryCommentId: data.threadId,
				level: data.level,
				targetId: data.targetId,
				order,
			})
		)
		return refetch()
	}, [data, order, refetch])

	const handleComment = useCallback((toComment: ReplyToComment) => {
		const modalId = showCommentFormModal({
			replyToComment: toComment,
			onClose: () => {
				modals.close(modalId)
			},
			onSubmit: refresh,
		})
	}, [])

	if (!data) {
		return null
	}

	return (
		<Page title="评论详情" onReachPageBottom={fetchNextPage}>
			{isViewPostButtonShow && (
				<Link
					to="/u/$username/$type/$id"
					params={{ username, id, type: POST_TYPE_TO_ROUTE_MAPPING[post.type] }}
					css={css`
						display: block;
					`}
				>
					<Button
						appearance="ghost"
						size="sm"
						css={css`
							margin: 16px auto;
						`}
					>
						查看原动态
					</Button>
				</Link>
			)}
			<div
				css={css`
					display: grid;
					grid-template-rows: auto 1fr;
					gap: 20px;
					overflow: hidden;
					width: 100%;
				`}
				key={data.id}
			>
				<CommentActionsProvider
					value={{
						refresh,
						onComment: handleComment,
					}}
				>
					<Comment
						noReplies
						data={{
							id: data.id,
							targetType: data.targetType,
						}}
						css={css`
							padding: 10px 15px;
						`}
						noSuffixTitle
					/>

					<CommentListSection
						data={comments}
						order={order}
						onOrderChange={setOrder}
						isLoading={isCommentListLoading}
					/>
				</CommentActionsProvider>
			</div>
		</Page>
	)
}
